/**
 * Apresentação executiva — /apresentacao.
 *
 * Um deck em tela cheia, estilo PPT, para a Canal/Rentex apresentar a
 * plataforma à diretoria da EGBA e a parceiros. Duas regras de conteúdo:
 *
 *  - **Executivo, não técnico.** Nenhum termo de máquina; o que aparece é o
 *    negócio: a lei, o mercado, as jornadas, o resultado.
 *  - **Números reais.** Os indicadores vêm da mesma API do painel
 *    (/mercado/resumo, /leads/resumo) — o deck nunca mostra número inventado;
 *    enquanto carrega, mostra traço, não zero.
 *
 * Identidade: branco + o azul #0070c0 do logo da Canal, extraído do próprio
 * PNG. Navegação: setas/espaço/clique; F11 do navegador ou o botão de tela
 * cheia; Esc sai do modo tela cheia e, fora dele, volta ao painel.
 */

import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../api/cliente'

const AZUL = '#0070c0'
const AZUL_ESCURO = '#004d85'
const AZUL_CLARO = '#e8f2fb'

const brl = (c: number): string =>
  (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
const num = (n: number): string => n.toLocaleString('pt-BR')

interface DadosApresentacao {
  ano: number
  escolasNoUniverso: number
  resumo: { escolas: number; concluintes: number; valorCentavos: number; escolasPara80Pct: number }
  porMunicipio?: Array<{ municipio: string; escolas: number; valorCentavos: number }>
  baseEmpresas: number
  baseEnsino?: number
  baseCompanhias?: number
}

// ---------------------------------------------------------------------------
// Peças visuais
// ---------------------------------------------------------------------------

function Marcador({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-xs font-semibold tracking-[0.22em] uppercase md:text-sm"
      style={{ color: AZUL }}
    >
      {children}
    </p>
  )
}

function Tile({ valor, rotulo, fonte }: { valor: string; rotulo: string; fonte?: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm md:p-8">
      <p
        className={`font-bold tracking-tight text-slate-900 tabular ${
          valor.length > 9 ? 'text-2xl md:text-4xl' : 'text-3xl md:text-5xl'
        }`}
      >
        {valor}
      </p>
      <p className="mt-2 text-sm leading-snug text-slate-600 md:mt-3 md:text-lg">{rotulo}</p>
      {fonte && <p className="mt-2 text-xs text-slate-400">{fonte}</p>}
    </div>
  )
}

function ItemLista({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <li className="flex gap-4 md:gap-5">
      <span
        aria-hidden="true"
        className="mt-2.5 block h-3 w-3 shrink-0 rounded-full"
        style={{ background: AZUL }}
      />
      <div>
        <p className="text-xl font-semibold text-slate-900 md:text-2xl">{titulo}</p>
        <p className="mt-1 text-base leading-relaxed text-slate-600 md:text-lg">{texto}</p>
      </div>
    </li>
  )
}

/**
 * Rosca de participação — parcela do potencial por município.
 *
 * Fatias ordenadas da maior para a menor num degradê de UM azul (a cor mostra
 * a ordem de grandeza; a identidade mora na legenda ao lado, com nome, valor e
 * percentual — nunca só na cor). "Outros" é cinza neutro. Traço de 2px entre
 * fatias, como manda a regra de marcas.
 */
const CORES_ROSCA = ['#004d85', '#0070c0', '#3d95d6', '#7ab7e4', '#b3d6f0']
const COR_OUTROS = '#cbd5e1'

function Rosca({ fatias }: { fatias: Array<{ rotulo: string; valor: number }> }) {
  const total = fatias.reduce((a, f) => a + f.valor, 0) || 1
  const R = 15.9155 // raio cuja circunferência é 100 — dasharray vira %
  let acumulado = 0
  return (
    <svg viewBox="0 0 42 42" className="h-52 w-52 md:h-64 md:w-64" role="img" aria-label="Participação por município">
      <circle cx="21" cy="21" r={R} fill="none" stroke="#f1f5f9" strokeWidth="7" />
      {fatias.map((f, i) => {
        const pct = (f.valor / total) * 100
        const el = (
          <circle
            key={f.rotulo}
            cx="21"
            cy="21"
            r={R}
            fill="none"
            stroke={i === fatias.length - 1 ? COR_OUTROS : CORES_ROSCA[i] ?? COR_OUTROS}
            strokeWidth="7"
            strokeDasharray={`${Math.max(0, pct - 1.2)} ${100 - Math.max(0, pct - 1.2)}`}
            strokeDashoffset={-(acumulado + 0.6)}
            transform="rotate(-90 21 21)"
          />
        )
        acumulado += pct
        return el
      })}
    </svg>
  )
}

// ---------------------------------------------------------------------------
// O deck
// ---------------------------------------------------------------------------

export function Apresentacao() {
  const navegar = useNavigate()
  const [indice, setIndice] = useState(0)
  const palco = useRef<HTMLDivElement>(null)
  const [telaCheia, setTelaCheia] = useState(false)

  // Endpoint público: o deck abre por link, sem login — só agregados saem.
  const { data: dados } = useQuery({
    queryKey: ['apresentacao-publica'],
    queryFn: () => api.get<{ dados: DadosApresentacao }>('/publico/apresentacao'),
    retry: 1,
  })
  const m = dados?.dados
  const ano = m?.ano ?? 2025
  const universo = m ? num(m.escolasNoUniverso) : '—'
  const comFormandos = m ? num(m.resumo.escolas) : '—'
  const potencial = m ? brl(m.resumo.valorCentavos) : '—'
  const prioritarias = m ? num(m.resumo.escolasPara80Pct) : '—'
  const formandos = m ? num(m.resumo.concluintes) : '—'
  const base = m ? num(m.baseEmpresas) : '—'
  const baseEnsino = m ? num(m.baseEnsino ?? m.baseEmpresas) : '—'

  // Top 5 municípios por potencial + "Outros" — a fatia neutra fecha o todo.
  const municipios = (() => {
    const lista = m?.porMunicipio ?? []
    const top = lista.slice(0, 5)
    const resto = lista.slice(5).reduce((a, x) => a + x.valorCentavos, 0)
    const totalTop = top.reduce((a, x) => a + x.valorCentavos, 0)
    const totalGeral = m?.resumo.valorCentavos ?? totalTop + resto
    const outros = Math.max(0, totalGeral - totalTop)
    const fatias = top.map((x) => ({ rotulo: x.municipio, valor: x.valorCentavos }))
    if (outros > 0) fatias.push({ rotulo: 'Outros municípios', valor: outros })
    return { fatias, totalGeral }
  })()

  // ------------------------------------------------------------- slides ---
  const slides: React.ReactNode[] = [
    // 1 · capa
    <div key="capa" className="m-auto flex w-full flex-col items-center text-center">
      <img src="/marca-canal.png" alt="Canal" className="h-10 md:h-16" />
      <h1 className="mt-8 max-w-5xl md:mt-14 text-4xl leading-[1.1] sm:text-5xl md:text-7xl md:leading-[1.05] font-bold tracking-tight text-slate-900">
        Plataforma de{' '}
        <span style={{ color: AZUL }}>Gestão Ativa</span>
        <br />
        do Diário Oficial
      </h1>
      <p className="mt-5 text-lg text-slate-500 md:mt-8 md:text-2xl">
        EGBA — Empresa Gráfica da Bahia · Governo do Estado
      </p>
      <p className="mt-16 text-sm tracking-[0.25em] text-slate-400 uppercase">
        Desenvolvida por Canal · Rentex
      </p>
    </div>,

    // 2 · o contexto
    <div key="lei" className="m-auto w-full">
      <Marcador>O ponto de partida</Marcador>
      <h2 className="mt-6 max-w-5xl text-3xl leading-[1.15] sm:text-4xl md:text-6xl md:leading-[1.08] font-bold text-slate-900">
        Publicar no Diário Oficial não é escolha.
        <br />
        <span style={{ color: AZUL }}>É obrigação legal.</span>
      </h2>
      <div
        className="mt-8 max-w-4xl rounded-2xl border-l-8 p-5 md:mt-12 md:p-8"
        style={{ borderColor: AZUL, background: AZUL_CLARO }}
      >
        <p className="text-lg leading-relaxed text-slate-800 md:text-2xl">
          Toda instituição de ensino da Bahia deve publicar a relação nominal dos concluintes do
          Ensino Médio e da Educação Profissional Técnica — e, nas escolas privadas,{' '}
          <strong>o custo é da própria escola</strong>.
        </p>
        <p className="mt-4 text-base text-slate-500">Resolução CEE/BA nº 44/2020, Art. 4º e § 1º</p>
      </div>
      <p className="mt-6 max-w-4xl text-lg leading-relaxed text-slate-600 md:mt-10 md:text-2xl">
        A maior parte dessas escolas nunca publicou. Cada turma formada é receita que a EGBA
        deixa de receber — <strong className="text-slate-900">todos os anos</strong>.
      </p>
    </div>,

    // 3 · o mercado — as duas bases, a rosca por município e o potencial
    <div key="mercado" className="m-auto w-full">
      <Marcador>O tamanho da oportunidade</Marcador>
      <h2 className="mt-4 text-3xl font-bold text-slate-900 sm:text-4xl md:mt-6 md:text-6xl">
        Um mercado medido, escola por escola
      </h2>
      <div className="mt-8 grid gap-8 md:mt-12 lg:grid-cols-[1fr_auto] lg:gap-14">
        <div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6">
            <Tile valor={universo} rotulo="escolas privadas declarando ao Censo Escolar (INEP)" />
            <Tile valor={baseEnsino} rotulo="CNPJs de ensino na base — integrada à Receita Federal" />
            <Tile
              valor={comFormandos}
              rotulo={`com formandos declarados — ${formandos} alunos concluindo por ano`}
            />
            <Tile valor={potencial} rotulo="em publicações potenciais por ano, já medidas" />
          </div>
          <div
            className="mt-6 rounded-2xl border-l-8 p-5 md:p-6"
            style={{ borderColor: AZUL, background: AZUL_CLARO }}
          >
            <p className="text-base leading-relaxed text-slate-700 md:text-lg">
              O potencial acima é calculado <strong>somente sobre quem declara alunos ao INEP</strong>{' '}
              e às demais fontes oficiais. A base integrada à Receita mostra{' '}
              <strong>{baseEnsino} CNPJs de ensino</strong> — há mais escolas do que as que
              declaram, e o potencial real tende a ser <strong>ainda maior</strong>.
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center gap-5 lg:pt-2">
          <Rosca fatias={municipios.fatias} />
          <ul className="w-full max-w-xs space-y-1.5">
            {municipios.fatias.map((f, i) => (
              <li key={f.rotulo} className="flex items-center gap-2.5 text-sm md:text-base">
                <span
                  aria-hidden="true"
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{
                    background:
                      i === municipios.fatias.length - 1 ? COR_OUTROS : (CORES_ROSCA[i] ?? COR_OUTROS),
                  }}
                />
                <span className="min-w-0 flex-1 truncate text-slate-700">{f.rotulo}</span>
                <span className="text-slate-500 tabular">
                  {municipios.totalGeral ? Math.round((f.valor / municipios.totalGeral) * 100) : 0}%
                </span>
              </li>
            ))}
          </ul>
          <p className="text-center text-xs text-slate-400">potencial anual por município</p>
        </div>
      </div>
      <p className="mt-6 text-sm text-slate-400 md:mt-8 md:text-base">
        Fontes oficiais: Censo Escolar {ano} (INEP) e base pública da Receita Federal — cruzadas e
        atualizadas pela plataforma.
      </p>
    </div>,

    // 4 · inteligência de dados
    <div key="dados" className="m-auto w-full">
      <Marcador>Inteligência de dados</Marcador>
      <h2 className="mt-6 max-w-5xl text-3xl leading-[1.15] sm:text-4xl md:text-6xl md:leading-[1.08] font-bold text-slate-900">
        A plataforma sabe <span style={{ color: AZUL }}>quem</span> precisa publicar —
        e como chegar lá
      </h2>
      <ul className="mt-8 max-w-5xl space-y-6 md:mt-14 md:space-y-8">
        <ItemLista
          titulo="Integração direta com a Receita Federal"
          texto="Cada escola é identificada pelo CNPJ, com endereço, situação e contatos oficiais — sempre atualizados."
        />
        <ItemLista
          titulo="Fontes públicas de educação"
          texto="O Censo Escolar diz quais escolas têm formandos e quantos — a cobrança vai para quem de fato deve publicar."
        />
        <ItemLista
          titulo="Higienização e enriquecimento"
          texto="Destinatários validados e qualificados, com parâmetros que levam até sócios e contatos estratégicos do administrativo e do financeiro."
        />
      </ul>
    </div>,

    // 5 · jornadas
    <div key="jornadas" className="m-auto w-full">
      <Marcador>Jornadas digitais completas</Marcador>
      <h2 className="mt-6 max-w-5xl text-3xl leading-[1.15] sm:text-4xl md:text-6xl md:leading-[1.08] font-bold text-slate-900">
        Cada escola percorre uma jornada —{' '}
        <span style={{ color: AZUL }}>no tom certo, na hora certa</span>
      </h2>
      <ul className="mt-8 max-w-5xl space-y-6 md:mt-14 md:space-y-8">
        <ItemLista
          titulo="E-mail, WhatsApp e SMS com identidade profissional"
          texto="Comunicações em três tons — do aviso cordial à notificação formal — sempre citando a norma que obriga a publicação."
        />
        <ItemLista
          titulo="Páginas de captação próprias"
          texto="A escola entende a obrigação, envia a relação de formandos e acompanha tudo num só lugar."
        />
        <ItemLista
          titulo="Sequências que reagem ao comportamento"
          texto="Visitou a página? Respondeu? Preencheu? O próximo passo muda conforme a reação de cada escola."
        />
      </ul>
    </div>,

    // 6 · 24/7
    <div key="autonomia" className="m-auto flex w-full flex-col items-center text-center">
      <Marcador>Operação contínua</Marcador>
      <h2 className="mt-8 max-w-5xl text-4xl leading-[1.1] sm:text-5xl md:text-7xl md:leading-[1.05] font-bold text-slate-900">
        Funciona sozinha.
        <br />
        <span style={{ color: AZUL }}>24 horas, 7 dias.</span>
      </h2>
      <p className="mt-6 max-w-4xl text-lg leading-relaxed text-slate-600 md:mt-10 md:text-2xl">
        Campanhas rodando dia e noite, públicos de retargeting alimentados automaticamente no
        Google e na Meta, e cada aviso acompanhado até virar publicação. A tecnologia aquece o
        interesse — <strong className="text-slate-900">a equipe entra quando ele aparece</strong>.
      </p>
    </div>,

    // 7 · funil (série única, rótulo direto — sem legenda)
    <div key="funil" className="m-auto w-full">
      <Marcador>Do universo à publicação</Marcador>
      <h2 className="mt-4 text-3xl font-bold text-slate-900 sm:text-4xl md:mt-6 md:text-6xl">Foco em quem vale mais</h2>
      <div className="mt-8 max-w-5xl md:mt-14 space-y-5">
        {[
          { rotulo: `Escolas privadas na Bahia`, valor: m?.escolasNoUniverso ?? 0, texto: universo },
          { rotulo: 'Com formandos do Médio e Técnico', valor: m?.resumo.escolas ?? 0, texto: comFormandos },
          {
            rotulo: 'Concentram 80% da receita potencial',
            valor: m?.resumo.escolasPara80Pct ?? 0,
            texto: prioritarias,
          },
        ].map((f, i, lista) => {
          const maior = lista[0]!.valor || 1
          const largura = Math.max(4, Math.round((f.valor / maior) * 100))
          // Rótulo direto: dentro da barra quando cabe; fora dela, em tinta
          // escura, quando a barra é estreita — número cortado não é número.
          const dentro = largura >= 22
          return (
            <div key={f.rotulo} className="flex flex-col gap-2 md:flex-row md:items-center md:gap-6">
              <div className="relative h-12 w-full overflow-hidden rounded-xl bg-slate-100 md:h-16 md:flex-1">
                <div
                  className="flex h-full items-center justify-end rounded-r px-5"
                  style={{ width: `${largura}%`, background: i === 2 ? AZUL_ESCURO : AZUL }}
                >
                  {dentro && (
                    <span className="text-lg font-bold whitespace-nowrap text-white tabular md:text-xl">
                      {f.texto}
                    </span>
                  )}
                </div>
                {!dentro && (
                  <span
                    className="absolute top-1/2 -translate-y-1/2 text-lg font-bold whitespace-nowrap text-slate-900 tabular md:text-xl"
                    style={{ left: `calc(${largura}% + 16px)` }}
                  >
                    {f.texto}
                  </span>
                )}
              </div>
              <p className="text-base leading-snug text-slate-600 md:w-80 md:text-lg">{f.rotulo}</p>
            </div>
          )
        })}
        <div className="flex flex-col gap-2 pt-2 md:flex-row md:items-center md:gap-6">
          <div
            className="flex h-12 w-full items-center justify-center rounded-xl border-2 border-dashed md:h-16 md:flex-1"
            style={{ borderColor: AZUL }}
          >
            <span className="px-3 text-center text-base font-semibold md:text-xl" style={{ color: AZUL_ESCURO }}>
              → Publicação no Diário Oficial
            </span>
          </div>
          <p className="text-base leading-snug text-slate-600 md:w-80 md:text-lg">
            acompanhada uma a uma, do primeiro aviso ao comprovante
          </p>
        </div>
      </div>
      <p className="mt-8 text-base text-slate-400">Censo Escolar {ano} · valores da tabela oficial da EGBA</p>
    </div>,

    // 8 · medição
    <div key="medicao" className="m-auto w-full">
      <Marcador>Resultado mensurável</Marcador>
      <h2 className="mt-6 max-w-5xl text-3xl leading-[1.15] sm:text-4xl md:text-6xl md:leading-[1.08] font-bold text-slate-900">
        Cada real investido tem <span style={{ color: AZUL }}>rastro</span>
      </h2>
      <div className="mt-8 grid max-w-5xl grid-cols-1 gap-3 sm:grid-cols-2 md:mt-14 lg:grid-cols-4 lg:gap-4">
        {['Recebeu o aviso', 'Abriu e visitou', 'Enviou os dados', 'Publicou'].map((etapa, i) => (
          <div key={etapa} className="flex items-center gap-3">
            <div
              className="flex-1 rounded-xl p-4 text-center md:p-6"
              style={{ background: i === 3 ? AZUL : AZUL_CLARO }}
            >
              <p
                className="text-lg leading-snug font-semibold"
                style={{ color: i === 3 ? '#fff' : AZUL_ESCURO }}
              >
                {etapa}
              </p>
            </div>
            {i < 3 && (
              <span aria-hidden="true" className="hidden text-2xl text-slate-300 lg:inline">
                →
              </span>
            )}
          </div>
        ))}
      </div>
      <p className="mt-8 max-w-4xl text-lg leading-relaxed text-slate-600 md:mt-12 md:text-2xl">
        A plataforma mostra, escola por escola, em que ponto da jornada cada uma está — e os
        públicos de anúncio se ajustam sozinhos a isso. Mídia deixa de ser aposta.
      </p>
      <p className="mt-6 text-lg text-slate-500">
        Base atual: <strong className="text-slate-700">{base}</strong> empresas qualificadas —
        escolas e companhias S.A. — prontas para as jornadas.
      </p>
    </div>,

    // 9 · conformidade
    <div key="regras" className="m-auto w-full">
      <Marcador>Segurança jurídica</Marcador>
      <h2 className="mt-6 max-w-5xl text-3xl leading-[1.15] sm:text-4xl md:text-6xl md:leading-[1.08] font-bold text-slate-900">
        Toda mensagem sai <span style={{ color: AZUL }}>dentro das regras</span>
      </h2>
      <ul className="mt-8 max-w-5xl space-y-6 md:mt-14 md:space-y-8">
        <ItemLista
          titulo="LGPD por desenho"
          texto="Consentimento registrado, descadastro em um clique e dados de alunos protegidos em área restrita."
        />
        <ItemLista
          titulo="Período eleitoral respeitado automaticamente"
          texto="Avisos de obrigação legal seguem permitidos; peças institucionais e promocionais são bloqueadas na janela vedada — sem depender de ninguém lembrar."
        />
        <ItemLista
          titulo="WhatsApp somente com autorização"
          texto="Nenhuma mensagem fria: o canal só é usado com opt-in registrado, protegendo o número e a marca da EGBA."
        />
      </ul>
    </div>,

    // 10 · atendimento
    <div key="atendimento" className="m-auto w-full">
      <Marcador>Tecnologia + atendimento</Marcador>
      <h2 className="mt-6 max-w-5xl text-3xl leading-[1.15] sm:text-4xl md:text-6xl md:leading-[1.08] font-bold text-slate-900">
        A jornada digital aquece.
        <br />
        <span style={{ color: AZUL }}>O atendimento conclui.</span>
      </h2>
      <p className="mt-6 max-w-4xl text-lg leading-relaxed text-slate-600 md:mt-10 md:text-2xl">
        Quando a escola demonstra interesse, entra o atendimento ativo da{' '}
        <strong className="text-slate-900">Canal</strong> — por telefone e WhatsApp — e os canais
        oficiais indicados pela EGBA, conduzindo cada caso até a publicação no Diário Oficial.
      </p>
      <div className="mt-8 flex max-w-4xl flex-col gap-4 rounded-2xl p-5 sm:flex-row sm:items-center md:mt-12 md:gap-8 md:p-8" style={{ background: AZUL_CLARO }}>
        <div className="text-5xl" aria-hidden="true">
          📞
        </div>
        <p className="text-base leading-relaxed break-words text-slate-700 md:text-xl">
          publicacoes@egba.ba.gov.br · (71) 3116-2850 · 3116-2133 · 3116-2863
          <br />
          <span className="text-base text-slate-500">
            os mesmos canais presentes em todas as comunicações e páginas
          </span>
        </p>
      </div>
    </div>,

    // 11 · fechamento
    <div key="fim" className="m-auto flex w-full flex-col items-center text-center">
      <h2 className="max-w-5xl text-4xl leading-[1.1] sm:text-5xl md:text-7xl md:leading-[1.05] font-bold text-slate-900">
        Receita que a lei já garante,
        <br />
        <span style={{ color: AZUL }}>realizada com método.</span>
      </h2>
      <p className="mt-6 text-lg text-slate-500 md:mt-10 md:text-2xl">
        Plataforma de Gestão Ativa do Diário Oficial · EGBA
      </p>
      <img src="/marca-canal.png" alt="Canal" className="mt-10 h-9 md:mt-16 md:h-12" />
      <p className="mt-4 text-sm tracking-[0.25em] text-slate-400 uppercase">Canal · Rentex</p>
    </div>,
  ]

  const total = slides.length

  const ir = useCallback(
    (passo: number) => setIndice((i) => Math.min(total - 1, Math.max(0, i + passo))),
    [total],
  )

  useEffect(() => {
    function aoTeclar(e: KeyboardEvent): void {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') ir(1)
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') ir(-1)
      else if (e.key === 'Home') setIndice(0)
      else if (e.key === 'End') setIndice(total - 1)
      else if (e.key === 'Escape' && !document.fullscreenElement) void navegar('/')
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [ir, navegar, total])

  useEffect(() => {
    const aoMudar = () => setTelaCheia(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', aoMudar)
    return () => document.removeEventListener('fullscreenchange', aoMudar)
  }, [])

  function alternarTelaCheia(): void {
    if (document.fullscreenElement) void document.exitFullscreen()
    else void palco.current?.requestFullscreen()
  }

  return (
    <div ref={palco} className="fixed inset-0 z-[70] flex flex-col bg-white select-none">
      <style>{`
        @keyframes slide-entra { from { opacity: 0; transform: translateX(28px); } to { opacity: 1; transform: none; } }
      `}</style>

      {/* ------------------------------------------------------------ palco */}
      <div className="relative min-h-0 flex-1">
        <div
          key={indice}
          className="absolute inset-0 flex overflow-y-auto px-6 py-8 md:px-[7vw] md:py-[6vh]"
          style={{ animation: 'slide-entra .45s ease-out both' }}
        >
          {slides[indice]}
        </div>

        {/* zonas de clique: metade esquerda volta, direita avança */}
        <button
          type="button"
          aria-label="Slide anterior"
          onClick={() => ir(-1)}
          className="absolute inset-y-0 left-0 w-1/4 cursor-w-resize opacity-0"
        />
        <button
          type="button"
          aria-label="Próximo slide"
          onClick={() => ir(1)}
          className="absolute inset-y-0 right-0 w-2/4 cursor-e-resize opacity-0"
        />

        {/* Setas visíveis: as zonas invisíveis funcionam, mas ninguém adivinha
            que existem — quem recebe o link precisa VER por onde passar. */}
        {indice > 0 && (
          <button
            type="button"
            aria-label="Slide anterior"
            onClick={() => ir(-1)}
            className="absolute top-1/2 left-2 z-20 flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-2xl text-slate-400 shadow-sm backdrop-blur transition hover:text-slate-700 hover:shadow md:left-4"
          >
            ‹
          </button>
        )}
        {indice < total - 1 && (
          <button
            type="button"
            aria-label="Próximo slide"
            onClick={() => ir(1)}
            className="absolute top-1/2 right-2 z-20 flex size-11 -translate-y-1/2 items-center justify-center rounded-full border shadow-sm backdrop-blur transition hover:shadow-md md:right-4"
            style={{ background: 'rgba(255,255,255,0.92)', borderColor: AZUL, color: AZUL }}
          >
            <span className="text-2xl">›</span>
          </button>
        )}
      </div>

      {/* ------------------------------------------------------------ rodapé */}
      <div className="flex shrink-0 items-center gap-3 border-t border-slate-100 px-4 py-3 md:gap-5 md:px-8 md:py-4">
        <img src="/marca-canal.png" alt="Canal · Rentex" className="h-5 opacity-80 md:h-6" />
        <div className="hidden items-center gap-1.5 sm:flex" role="tablist" aria-label="Slides">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Slide ${i + 1}`}
              onClick={() => setIndice(i)}
              className="rounded-full transition-all"
              style={{
                width: i === indice ? 22 : 8,
                height: 8,
                background: i === indice ? AZUL : '#cbd5e1',
              }}
            />
          ))}
        </div>
        <span className="text-sm text-slate-400 tabular">
          {indice + 1} / {total}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={alternarTelaCheia}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50"
          >
            {telaCheia ? 'Sair da tela cheia' : 'Tela cheia'}
          </button>
          {!telaCheia && (
            <button
              type="button"
              onClick={() => void navegar('/')}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50"
            >
              Fechar
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
